import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useColorScheme } from '@mui/joy';
import { APPEARANCE_KEY, INITIAL_APPEARANCE, MODES, applyAppearance, nextAppearanceBoundary } from './appearance';

const AppearanceContext = createContext(null);

export default function AppearanceProvider({ children }) {
  const { setMode } = useColorScheme();
  const [preference, setPreference] = useState(INITIAL_APPEARANCE.preference);
  const [colorScheme, setColorScheme] = useState(INITIAL_APPEARANCE.colorScheme);

  const changePreference = useCallback(value => {
    if (!MODES.includes(value)) return;
    setPreference(value);
    try { localStorage.setItem(APPEARANCE_KEY, value); } catch { /* Keep working without storage. */ }
  }, []);

  useLayoutEffect(() => {
    const scheme = applyAppearance(preference);
    setColorScheme(scheme);
    setMode(scheme);
  }, [preference, setMode]);

  useEffect(() => {
    let timer;
    function refresh() {
      clearTimeout(timer);
      const scheme = applyAppearance(preference);
      setColorScheme(scheme);
      setMode(scheme);
      if (preference === 'auto') {
        // Minute cap also notices a manual clock/timezone change. The boundary
        // timeout switches at 07:00/20:00 without needing a reload.
        const delay = Math.min(60000, Math.max(50, nextAppearanceBoundary().getTime() - Date.now()));
        timer = setTimeout(refresh, delay);
      }
    }
    function sync(event) {
      if (event.key === APPEARANCE_KEY || event.key === null) {
        setPreference(MODES.includes(event.newValue) ? event.newValue : 'auto');
      }
    }
    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('storage', sync);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('storage', sync);
    };
  }, [preference, setMode]);

  const value = useMemo(() => ({ preference, colorScheme, setPreference: changePreference }), [preference, colorScheme, changePreference]);
  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export const useAppearance = () => useContext(AppearanceContext);
